// apps/client/src/services/accounts.ts
import {
  AccountClass,
  AccountType,
  ActivityAllocation,
  ProjectAccountsResponse,
  ProjectAccountAllocationDeleteResponse,
  AccountsFetchResponse,
  ProjectAccountAllocationUpdateRequest,
} from '@avanzu/types';
import { apiClient } from './apiClient';

/**
 * Fetch all accounts for an organisation (general accounts endpoint)
 */
export const fetchAccounts = async (
  organisationId: string,
  filters?: {
    types?: AccountType[];
    classes?: AccountClass[];
    allocatedOnly?: boolean;
  }
): Promise<AccountsFetchResponse> => {
  // Build query parameters
  const params = new URLSearchParams();

  if (filters?.types && filters.types.length > 0) {
    params.append('types', filters.types.join(','));
  }

  if (filters?.classes && filters.classes.length > 0) {
    params.append('classes', filters.classes.join(','));
  }

  if (filters?.allocatedOnly !== undefined) {
    params.append('allocatedOnly', filters.allocatedOnly.toString());
  }

  const queryString = params.toString();
  const url = `/organisations/${organisationId}/accounts${queryString ? `?${queryString}` : ''}`;

  const response = await apiClient.get(url);
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.message || 'Failed to fetch accounts');
  }
  return response.json();
};

/**
 * Fetch project accounts (accounts with project-specific information)
 */
export const fetchProjectAccounts = async (
  organisationId: string,
  projectId: string,
  filters?: {
    types?: AccountType[];
    classes?: AccountClass[];
    allocatedOnly?: boolean;
  }
): Promise<ProjectAccountsResponse> => {
  const params = new URLSearchParams();

  if (filters?.types && filters.types.length > 0) {
    params.append('types', filters.types.join(','));
  }

  if (filters?.classes && filters.classes.length > 0) {
    params.append('classes', filters.classes.join(','));
  }

  if (filters?.allocatedOnly !== undefined) {
    params.append('allocatedOnly', filters.allocatedOnly.toString());
  }

  const queryString = params.toString();

  const response = await apiClient.get(
    `/organisations/${organisationId}/projects/${projectId}/accounts${queryString ? `?${queryString}` : ''}`
  );

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.message || 'Failed to fetch project accounts');
  }
  return response.json();
};

/**
 * Save account allocation rules for a project
 */
export const saveAccountAllocations = async (
  organisationId: string,
  projectId: string,
  accountId: string,
  allocations: ActivityAllocation[],
  exclude?: boolean
): Promise<void> => {
  const requestBody: ProjectAccountAllocationUpdateRequest = {
    accountId,
    projectId,
    allocations,
    isExcluded: exclude || allocations.length === 0,
  };

  const response = await apiClient.put(
    `/organisations/${organisationId}/projects/${projectId}/accounts/${accountId}/allocations`,
    requestBody
  );

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.message || 'Failed to save account allocations');
  }
};

/**
 * Delete account allocation rules for a project
 */
export const deleteAccountAllocations = async (
  organisationId: string,
  projectId: string,
  accountId: string
): Promise<ProjectAccountAllocationDeleteResponse> => {
  const response = await apiClient.delete(
    `/organisations/${organisationId}/projects/${projectId}/accounts/${accountId}/allocations`
  );

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.message || 'Failed to delete account allocations');
  }

  return response.json();
};
